import { UUID } from "crypto";

import type { JSONContent } from "@tiptap/react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { Role } from "@demo/types/base";
import { BLANK } from "@demo/utils/editor";

type DocRoles = Record<Role, JSONContent>;

export interface DocumentStoreState {
  draftDocuments: Record<UUID, DocRoles>;
  setDraftDocumentToBlankJson: (uuid: UUID) => void;
  setDraftDocumentJson: (uuid: UUID, role: Role, json: JSONContent) => void;
  createDraftDocument: (uuid: UUID, initial?: Partial<DocRoles>) => void;
  deleteDocument: (uuid: UUID) => void;
}

export const useDocumentStore = create<DocumentStoreState>()(
  devtools(
    immer((set) => ({
      draftDocuments: {},

      setDraftDocumentToBlankJson: (uuid: UUID) => {
        set((state) => {
          state.draftDocuments[uuid] = {
            [Role.USER]: BLANK,
            [Role.SYSTEM]: BLANK,
          } as DocRoles;
        });
      },

      setDraftDocumentJson: (uuid: UUID, role: Role, json: JSONContent) =>
        set((state) => {
          if (typeof json !== "object" || json === null) return;
          if (!state.draftDocuments[uuid]) {
            state.draftDocuments[uuid] = {
              [Role.USER]: BLANK,
              [Role.SYSTEM]: BLANK,
            } as DocRoles;
          }
          state.draftDocuments[uuid][role] = json;
        }),

      createDraftDocument: (uuid: UUID, initial = {}) =>
        set((state) => {
          if (!state.draftDocuments[uuid]) {
            state.draftDocuments[uuid] = {
              [Role.USER]: initial[Role.USER] ?? BLANK,
              [Role.SYSTEM]: initial[Role.SYSTEM] ?? BLANK,
            } as DocRoles;
          }
        }),

      deleteDocument: (uuid: UUID) =>
        set((state) => {
          delete state.draftDocuments[uuid];
        }),
    })),
    { name: "documentStore" },
  ),
);
